import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { ShieldCheck, Flag, Quote, MapPin, GitCompareArrows, ChevronRight, Check } from "lucide-react";
import { draftApi } from "./useDraftDocument";

const GRADE = { A: "text-success", B: "text-copper", C: "text-warning", D: "text-danger" };

// Phase-3 depth: one review centre for the whole draft — authorities, propositions,
// pinpoints and citation consistency. Nothing is changed automatically; the advocate decides.
export function CitationHealth({ open, data, draftId, onClose, onInspect, onChanged }) {
  const h = data?.health;
  if (!open || !h) return null;
  const openCites = (data.alerts || []).filter((a) => !a.decision);
  const reviewedCites = (data.alerts || []).filter((a) => a.decision);
  const openProps = (data.propositionAlerts || []).filter((a) => !a.decision);
  const gaps = data.pinpointGaps || [];
  const incons = data.inconsistencies || [];

  const markProp = async (a) => {
    try { await draftApi.alertDecision(draftId, { key: a.key, action: "EDITED" }); toast.success("Marked as addressed."); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); }
  };

  const Stat = ({ label, value, tone }) => (
    <div className="rounded-lg border border-line bg-card p-2 text-center">
      <div className={cn("font-display text-xl text-ink", tone)} data-testid={`health-stat-${label.toLowerCase()}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-wide text-ink-muted">{label}</div>
    </div>
  );
  const Row = ({ icon: Icon, tone, title, sub, action, testid }) => (
    <div className={cn("flex items-start gap-2 rounded-lg border border-line border-l-2 p-2", tone)} data-testid={testid}>
      <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ink-2" />
      <div className="min-w-0 flex-1">
        <div className="truncate text-xs font-medium text-ink">{title}</div>
        {sub && <div className="text-[11px] leading-snug text-ink-2">{sub}</div>}
      </div>
      {action}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="citation-health">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-2xl"><ShieldCheck className="h-5 w-5 text-copper" /> Citation Health</DialogTitle>
          <DialogDescription>A live check of every authority and proposition the draft relies on. Contrary ≠ invalid — nothing is changed automatically; you decide.</DialogDescription>
        </DialogHeader>
        <div className="nyk-scroll max-h-[70vh] space-y-3 overflow-y-auto pr-1">
          <div className="flex items-center gap-4 rounded-lg border border-line p-3">
            <div className="text-center">
              <div className={cn("font-display text-4xl leading-none", GRADE[h.grade])} data-testid="health-grade">{h.grade}</div>
              <div className="text-[10px] text-ink-muted">grade</div>
            </div>
            <div className="flex-1">
              <div className="mb-1 flex items-center justify-between text-xs"><span className="text-ink-2">Readiness score</span><b className="text-ink" data-testid="health-score">{h.score}/100</b></div>
              <Progress value={h.score} className="h-2" />
              <div className="mt-2 flex items-center justify-between text-xs"><span className="text-ink-2">Pinpoint coverage</span><b className="text-ink">{h.pinpointCoverage}%</b></div>
              <Progress value={h.pinpointCoverage} className="mt-1 h-1.5" />
            </div>
          </div>
          <div className="grid grid-cols-4 gap-2">
            <Stat label="Citations" value={h.totalCitations} />
            <Stat label="Verified" value={h.verified} tone={h.verified ? "text-success" : ""} />
            <Stat label="Critical" value={h.red} tone={h.red ? "text-danger" : ""} />
            <Stat label="Review" value={h.orange} tone={h.orange ? "text-warning" : ""} />
          </div>

          {openCites.length > 0 && <div className="space-y-1.5">
            <div className="text-xs font-semibold uppercase tracking-wide text-ink-muted">Authorities needing review ({openCites.length})</div>
            {openCites.map((a) => <Row key={a.key} testid="health-cite-row" icon={Flag} tone={a.level === "RED" ? "border-l-danger bg-danger/5" : "border-l-warning bg-warning-bg/40"}
              title={a.title || a.citation || "Unverified citation"} sub={a.reasons?.[0]}
              action={<Button size="sm" variant="outline" className="h-7 shrink-0 text-[11px]" onClick={() => onInspect(a)} data-testid="health-inspect">Inspect <ChevronRight className="ml-0.5 h-3 w-3" /></Button>} />)}
          </div>}

          {openProps.length > 0 && <div className="space-y-1.5">
            <div className="text-xs font-semibold uppercase tracking-wide text-ink-muted">Propositions needing support ({openProps.length})</div>
            {openProps.map((a) => <Row key={a.key} testid="health-prop-row" icon={Quote} tone={a.level === "RED" ? "border-l-danger bg-danger/5" : "border-l-warning bg-warning-bg/40"}
              title={a.text} sub={a.reasons?.join(" · ")}
              action={<Button size="sm" variant="ghost" className="h-7 shrink-0 text-[11px]" onClick={() => markProp(a)} data-testid="health-prop-addressed"><Check className="mr-0.5 h-3 w-3" />Mark addressed</Button>} />)}
          </div>}

          {incons.length > 0 && <div className="space-y-1.5">
            <div className="text-xs font-semibold uppercase tracking-wide text-ink-muted">Inconsistent citations ({incons.length})</div>
            {incons.map((x, i) => <Row key={i} testid="health-incons-row" icon={GitCompareArrows} tone="border-l-warning" title={x.title || x.citation || "Citation mismatch"} sub={x.detail} />)}
          </div>}

          {gaps.length > 0 && <div className="space-y-1.5">
            <div className="text-xs font-semibold uppercase tracking-wide text-ink-muted">Missing pinpoints ({gaps.length})</div>
            {gaps.slice(0, 12).map((g, i) => <Row key={i} testid="health-pinpoint-row" icon={MapPin} tone="border-l-line" title={g.citation || "Citation"} sub={`${g.section || "Draft"} — cite the paragraph the proposition relies on`} />)}
          </div>}

          {!openCites.length && !openProps.length && !incons.length && !gaps.length &&
            <div className="rounded-lg border border-success/30 bg-success/5 p-4 text-center text-sm text-ink-2" data-testid="health-clear">Every citation is verified, consistent and pinpointed.{reviewedCites.length ? ` ${reviewedCites.length} reviewed with a recorded reason.` : ""}</div>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
